import { useState, type MouseEvent } from "react";
import { BaseFeeTiming, BillingPlanStatus } from "@api/shared-runtime/enums";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { BillingPlanCategoryFields } from "./billing-plan-category-fields";
import { BillingPlanPreview } from "./billing-plan-preview";
import {
  MONEY_STEP,
  getDefaultBaseFeeTiming,
  getPlanFormDefaults,
  getPlanGap,
  getPlanPreviewInput,
  formatPlanDate,
  type BillingPlanData,
  type BillingPlanFormValues,
  type BillingPlanPreview as Preview,
} from "./billing-plan-values";

const PLAN_POLICY_NOTE =
  "Cycles are anchored to the start date. The full base fee applies to every started cycle; there is no proration. Saving a plan does not create an invoice or charge a payment method.";
const FORM_TITLE_ID = "billing-plan-form-title";

export function BillingPlanForm({
  data,
  onSaved,
  onBusyChange,
  onReload,
  onCancel,
  autoFocus = false,
}: {
  data: BillingPlanData;
  onSaved: () => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  onReload: () => void;
  /** Hides the editor again; omitted when the unit has no plan to fall back on. */
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const trpc = useTRPC();
  const form = useForm<BillingPlanFormValues>({
    defaultValues: getPlanFormDefaults(data),
  });
  const startDate = useWatch({ control: form.control, name: "startDate" });
  const invoicingEnabled = useWatch({
    control: form.control,
    name: "invoicingEnabled",
  });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [timingChosen, setTimingChosen] = useState(false);
  const hasActivePlan = data.plans.some(
    (plan) => plan.status === BillingPlanStatus.Active,
  );
  const previewMutation = useMutation(
    trpc.adminBillingPlans.previewPlan.mutationOptions(),
  );
  const saveMutation = useMutation(
    trpc.adminBillingPlans.savePlan.mutationOptions({ onSuccess: onSaved }),
  );
  const isBusy = previewMutation.isPending || saveMutation.isPending;
  const replacesPlanToday =
    !data.canStartCurrentMonth && startDate === data.today;
  const error = saveMutation.error ?? previewMutation.error;
  const gap = getPlanGap({ data, startDate });

  function discardPreview() {
    setPreview(null);
    previewMutation.reset();
    saveMutation.reset();
  }

  // A new plan follows the fee until the admin picks a timing; existing
  // terms keep theirs so a replacement never silently changes when it bills.
  function followBaseFee(baseMonthlyAmount: string) {
    if (!timingChosen && !hasActivePlan) {
      form.setValue(
        "baseFeeTiming",
        getDefaultBaseFeeTiming(baseMonthlyAmount),
      );
    }
  }

  function requestPreview(event: MouseEvent<HTMLButtonElement>) {
    if (!event.currentTarget.form?.reportValidity()) {
      return;
    }
    void form.handleSubmit(previewPlan)();
  }

  async function previewPlan(values: BillingPlanFormValues) {
    const input = getPlanPreviewInput({ data, values });
    onBusyChange(true);
    setPreview(null);
    saveMutation.reset();
    try {
      setPreview(await previewMutation.mutateAsync(input));
    } catch {
      // The mutation error is rendered beside the action for a safe retry.
    } finally {
      onBusyChange(false);
    }
  }

  async function savePlan(values: BillingPlanFormValues) {
    if (isBusy) {
      return;
    }
    const input = getPlanPreviewInput({ data, values });
    onBusyChange(true);
    previewMutation.reset();
    try {
      await saveMutation.mutateAsync(input);
    } catch {
      // Stale submissions show the server's conflict and a reload action below.
    } finally {
      onBusyChange(false);
    }
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(savePlan)}
        onChangeCapture={discardPreview}
        className="space-y-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
          <h3 id={FORM_TITLE_ID} className="font-semibold">
            {hasActivePlan ? "Schedule new terms" : "Set up plan"}
          </h3>
          {onCancel && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isBusy}
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{PLAN_POLICY_NOTE}</p>
        <fieldset
          disabled={isBusy}
          aria-labelledby={FORM_TITLE_ID}
          className="space-y-4 disabled:opacity-60"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="plan-start-date">Start date</Label>
              <Input
                id="plan-start-date"
                type="date"
                required
                autoFocus={autoFocus}
                min={data.minimumStartDate}
                {...form.register("startDate")}
              />
              <p className="text-xs text-muted-foreground">
                {replacesPlanToday
                  ? "New terms start immediately when saved today. Earlier usage keeps its previous pricing."
                  : "Starts at midnight on the selected date in New York."}
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="plan-base-fee">Base monthly fee (USD)</Label>
              <Input
                id="plan-base-fee"
                type="number"
                required
                min="0"
                step={MONEY_STEP}
                {...form.register("baseMonthlyAmount", {
                  onChange: (event) => followBaseFee(event.target.value),
                })}
              />
              <p className="text-xs text-muted-foreground">
                Use 0 for usage-only pricing.
              </p>
            </div>
          </div>
          {gap && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
              The existing end date stays in place. There will be no plan from{" "}
              {formatPlanDate(gap.firstDate)} through{" "}
              {formatPlanDate(gap.lastDate)}. Change the existing end date
              separately to cover that gap.
            </p>
          )}
          <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label
                htmlFor="plan-create-invoicing-enabled"
                className="font-semibold"
              >
                Invoicing
              </Label>
              <p className="text-xs text-muted-foreground">
                {invoicingEnabled
                  ? "Invoice drafts are generated from the first cycle."
                  : "Usage is reported only. Turn on later from the plan card."}
              </p>
            </div>
            <Controller
              control={form.control}
              name="invoicingEnabled"
              render={({ field }) => (
                <Switch
                  id="plan-create-invoicing-enabled"
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    discardPreview();
                    field.onChange(checked);
                  }}
                />
              )}
            />
          </div>
          {invoicingEnabled && (
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold">
                Base fee billing
              </legend>
              <Controller
                control={form.control}
                name="baseFeeTiming"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={(value) => {
                      setTimingChosen(true);
                      field.onChange(value);
                    }}
                    className="gap-3"
                  >
                    <div className="flex items-start gap-3">
                      <RadioGroupItem
                        value={BaseFeeTiming.InAdvance}
                        id="plan-timing-advance"
                        className="mt-0.5"
                      />
                      <div className="space-y-1">
                        <Label htmlFor="plan-timing-advance">
                          At the start of the cycle
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Base fee invoiced on day one; the previous
                          cycle&apos;s extra usage rides on the same invoice.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <RadioGroupItem
                        value={BaseFeeTiming.InArrears}
                        id="plan-timing-arrears"
                        className="mt-0.5"
                      />
                      <div className="space-y-1">
                        <Label htmlFor="plan-timing-arrears">
                          After the cycle ends
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          One invoice with the base fee and the cycle&apos;s
                          usage, the day after it ends.
                        </p>
                      </div>
                    </div>
                  </RadioGroup>
                )}
              />
            </fieldset>
          )}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Category pricing</h3>
            <p className="text-xs text-muted-foreground">
              Categories without a price are free.
            </p>
            {data.categories.map((category, index) => (
              <BillingPlanCategoryFields
                key={category.categoryCode}
                category={category}
                index={index}
                onChange={discardPreview}
              />
            ))}
          </div>
          <div className="space-y-1">
            <Label htmlFor="plan-notes">Internal notes</Label>
            <Textarea
              id="plan-notes"
              {...form.register("notes")}
              placeholder="Agreed commercial terms and reason for change"
            />
          </div>
        </fieldset>
        {preview && (
          <BillingPlanPreview preview={preview} categories={data.categories} />
        )}
        {error && (
          <div className="space-y-2">
            <p role="alert" className="text-sm text-red-700">
              {error.message}
            </p>
            {error.data?.code === "CONFLICT" && (
              <Button
                type="button"
                variant="outline"
                disabled={isBusy}
                onClick={onReload}
              >
                Reload latest plan
              </Button>
            )}
          </div>
        )}
        <div className="sticky -bottom-6 -mx-6 flex flex-wrap justify-end gap-2 border-t bg-background px-6 py-3">
          <Button
            type="button"
            variant="outline"
            disabled={isBusy}
            onClick={requestPreview}
          >
            {previewMutation.isPending ? "Preparing preview…" : "Preview plan"}
          </Button>
          <Button type="submit" disabled={isBusy}>
            {saveMutation.isPending ? "Saving…" : "Save plan"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
